import { trackCalculatorComplete } from "@/components/analytics/conversions";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { seo } from "@/utils/seo";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  Dumbbell,
  Info,
  Target,
  TrendingUp,
} from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/_app/essay/1rm-calculator")({
  component: OneRMCalculator,
  head: () => ({
    meta: seo({
      title: "1RM Hesaplayıcı - Tek Tekrar Maksimum Hesaplama | AtalayTheCoach",
      description:
        "Powerlifting performansınızı bilimsel formüllerle ölçün. Squat, bench press ve deadlift için güvenilir 1RM hesaplama aracı.",
      keywords:
        "1rm hesaplama, tek tekrar maksimum, powerlifting hesaplayıcı, squat 1rm, bench press 1rm, deadlift 1rm, güç sporları, Alican Atalay",
      canonical: "https://atalaythecoach.com/essay/1rm-calculator",
    }),
  }),
});

// 1RM Formülleri
const formulas = {
  epley: (weight: number, reps: number) => weight * (1 + reps / 30),
  brzycki: (weight: number, reps: number) => weight / (1.0278 - 0.0278 * reps),
  lander: (weight: number, reps: number) =>
    (100 * weight) / (101.3 - 2.67123 * reps),
  lombardi: (weight: number, reps: number) => weight * reps ** 0.1,
  mayhew: (weight: number, reps: number) =>
    (100 * weight) / (52.2 + 41.9 * Math.exp(-0.055 * reps)),
};

function OneRMCalculator() {
  const [weight, setWeight] = useState<string>("");
  const [reps, setReps] = useState<string>("");
  const [exercise, setExercise] = useState<string>("");
  const [results, setResults] = useState<Record<string, number> | null>(null);

  const calculateOneRM = () => {
    const w = Number.parseFloat(weight);
    const r = Number.parseInt(reps);

    if (!w || !r || r < 1 || r > 20) return;

    const calculated = {
      epley: formulas.epley(w, r),
      brzycki: formulas.brzycki(w, r),
      lander: formulas.lander(w, r),
      lombardi: formulas.lombardi(w, r),
      mayhew: formulas.mayhew(w, r),
    };

    setResults(calculated);
    trackCalculatorComplete("1rm");
  };

  const getAverage = () => {
    if (!results) return 0;
    const values = Object.values(results);
    return values.reduce((a, b) => a + b, 0) / values.length;
  };

  return (
    <div className="min-h-screen bg-background pt-32 pb-16">
      <div className="container mx-auto max-w-4xl px-4">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-primary/10 p-4 dark:bg-primary/20">
              <Dumbbell className="h-12 w-12 text-primary" />
            </div>
          </div>
          <h1 className="mb-6 font-bold text-4xl text-foreground leading-tight md:text-5xl lg:text-6xl">
            1RM Hesaplayıcı
          </h1>
          <p className="mx-auto max-w-3xl text-lg text-muted-foreground leading-relaxed md:text-xl">
            Tek tekrar maksimum (1RM) hesaplayıcı ile powerlifting
            performansınızı ölçün. Squat, bench press ve deadlift için güvenilir
            hesaplama.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-2">
          {/* Hesaplayıcı */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Dumbbell className="h-5 w-5 text-primary" />
                1RM Hesapla
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="exercise">Egzersiz Seçin</Label>
                <Select value={exercise} onValueChange={setExercise}>
                  <SelectTrigger>
                    <SelectValue placeholder="Egzersiz seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="squat">Squat</SelectItem>
                    <SelectItem value="bench">Bench Press</SelectItem>
                    <SelectItem value="deadlift">Deadlift</SelectItem>
                    <SelectItem value="overhead">Overhead Press</SelectItem>
                    <SelectItem value="other">Diğer</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="weight">Ağırlık (kg)</Label>
                <Input
                  id="weight"
                  type="number"
                  placeholder="100"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reps">Tekrar Sayısı</Label>
                <Input
                  id="reps"
                  type="number"
                  placeholder="5"
                  value={reps}
                  onChange={(e) => setReps(e.target.value)}
                  min="1"
                  max="20"
                />
              </div>

              <Button
                onClick={calculateOneRM}
                className="w-full"
                disabled={!weight || !reps}
              >
                1RM Hesapla
              </Button>
            </CardContent>
          </Card>

          {/* Sonuçlar */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                Sonuçlar
              </CardTitle>
            </CardHeader>
            <CardContent>
              {results ? (
                <div className="space-y-4">
                  <div className="rounded-lg bg-primary/10 p-4">
                    <div className="text-center">
                      <div className="font-bold text-2xl text-primary">
                        {getAverage().toFixed(1)} kg
                      </div>
                      <div className="text-muted-foreground text-sm">
                        Ortalama 1RM
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-semibold">Formül Sonuçları:</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        Epley:{" "}
                        <span className="font-mono">
                          {results.epley.toFixed(1)} kg
                        </span>
                      </div>
                      <div>
                        Brzycki:{" "}
                        <span className="font-mono">
                          {results.brzycki.toFixed(1)} kg
                        </span>
                      </div>
                      <div>
                        Lander:{" "}
                        <span className="font-mono">
                          {results.lander.toFixed(1)} kg
                        </span>
                      </div>
                      <div>
                        Lombardi:{" "}
                        <span className="font-mono">
                          {results.lombardi.toFixed(1)} kg
                        </span>
                      </div>
                      <div>
                        Mayhew:{" "}
                        <span className="font-mono">
                          {results.mayhew.toFixed(1)} kg
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-muted bg-muted/30 p-4">
                    <div className="flex items-start gap-3">
                      <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-muted-foreground" />
                      <p className="text-muted-foreground text-sm">
                        <strong className="text-foreground">
                          Güvenlik Uyarısı:
                        </strong>{" "}
                        1RM testleri riskli olabilir. Daima spotçı eşliğinde ve
                        uygun ısınma sonrası yapın.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  Hesaplama yapmak için sol taraftaki formu doldurun
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Modern Bilgilendirici İçerik */}
        <div className="mt-16 space-y-12">
          {/* Hero Section */}
          <div className="rounded-2xl border border-border/50 bg-card p-8 md:p-12">
            <div className="grid items-center gap-8 md:grid-cols-2">
              <div>
                <h2 className="mb-4 font-bold text-3xl text-foreground">
                  Gerçek Gücünüzü Keşfedin
                </h2>
                <p className="mb-6 text-lg text-muted-foreground">
                  1RM, powerlifting'in temel taşıdır. 5 farklı bilimsel formülle
                  en doğru maksimum gücünüzü hesaplayın.
                </p>
              </div>
              <div className="text-center">
                <div className="mb-4 flex justify-center">
                  <div className="rounded-full bg-primary/10 p-4 dark:bg-primary/20">
                    <Dumbbell className="h-12 w-12 text-primary" />
                  </div>
                </div>
                <div className="text-muted-foreground text-sm">
                  Bilimsel formüllerle doğru hesaplama
                </div>
              </div>
            </div>
          </div>

          {/* Formula Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                name: "Epley",
                accuracy: "95%",
                best: "Genel kullanım",
              },
              {
                name: "Brzycki",
                accuracy: "93%",
                best: "1-3 tekrar",
              },
              {
                name: "Lander",
                accuracy: "91%",
                best: "4-8 tekrar",
              },
              {
                name: "Lombardi",
                accuracy: "90%",
                best: "Logaritmik",
              },
              {
                name: "Mayhew",
                accuracy: "88%",
                best: "Bilimsel",
              },
            ].map((formula) => (
              <div className="group transition-all duration-300 hover:scale-105">
                <div
                  className={`rounded-xl border-2 border-muted-foreground bg-card p-4 hover:shadow-lg`}
                >
                  <h3 className="mb-2 font-bold text-foreground text-lg">
                    {formula.name}
                  </h3>
                  <div className="mb-1 text-muted-foreground text-sm">
                    Doğruluk: {formula.accuracy}
                  </div>
                  <div className="text-muted-foreground text-xs">
                    En iyi: {formula.best}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Intensity Zones */}
          <div className="rounded-2xl border border-border/50 bg-card p-8">
            <h3 className="mb-8 flex items-center justify-center gap-2 text-center font-bold text-2xl text-foreground">
              <Target className="h-6 w-6 text-primary" />
              Antrenman Yoğunluk Bölgeleri
            </h3>

            <div className="space-y-4">
              {[
                {
                  range: "95-100%",
                  purpose: "Pik Güç & Yarışma",
                  color: "bg-red-500",
                  description: "Maksimum güç için",
                },
                {
                  range: "85-95%",
                  purpose: "Maksimal Güç",
                  color: "bg-orange-500",
                  description: "Güç geliştirme",
                },
                {
                  range: "70-85%",
                  purpose: "Güç & Hipertrofi",
                  color: "bg-yellow-500",
                  description: "Kas ve güç",
                },
                {
                  range: "60-70%",
                  purpose: "Teknik & Hacim",
                  color: "bg-green-500",
                  description: "Form geliştirme",
                },
              ].map((zone) => (
                <div className="flex items-center gap-4 rounded-xl border p-4 transition-colors hover:border-primary/50 dark:border-border/30 dark:hover:border-primary/50">
                  <div className={`h-4 w-4 rounded-full ${zone.color}`}></div>
                  <div className="flex-1">
                    <div className="flex items-center gap-4">
                      <span className="font-bold text-foreground text-lg">
                        {zone.range}
                      </span>
                      <span className="font-semibold text-foreground">
                        {zone.purpose}
                      </span>
                    </div>
                    <p className="text-muted-foreground text-sm">
                      {zone.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Interactive Safety Guide */}
          <div className="rounded-2xl border border-border/50 bg-card p-8">
            <h3 className="mb-8 flex items-center gap-3 font-bold text-2xl text-foreground">
              <AlertTriangle className="h-7 w-7 text-destructive" />
              Güvenlik Rehberi
            </h3>

            <div className="grid gap-8 md:grid-cols-2">
              <div>
                <h4 className="mb-4 font-semibold text-foreground text-lg">
                  Yapılması Gerekenler
                </h4>
                <div className="space-y-4">
                  {[
                    "Deneyimli spotçı ile çalışın",
                    "20-30 dakika ısınma yapın",
                    "Kademeli olarak ağırlık artırın",
                    "Proper form'u koruyun",
                    "Yeterli dinlenme alın",
                  ].map((item) => (
                    <div className="flex items-start gap-4">
                      <div className="mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary">
                        <span className="font-bold text-primary-foreground text-sm">
                          ✓
                        </span>
                      </div>
                      <span className="text-base text-foreground leading-relaxed">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="mb-4 font-semibold text-foreground text-lg">
                  Yapılmaması Gerekenler
                </h4>
                <div className="space-y-4">
                  {[
                    "Yorgunken 1RM testi yapmayın",
                    "Formu bozmaya çalışmayın",
                    "Tek başına test yapmayın",
                    "Aşırı sık test yapmayın",
                    "Yaralanma riskini göz ardı etmeyin",
                  ].map((item) => (
                    <div className="flex items-start gap-4">
                      <div className="mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-destructive">
                        <span className="font-bold text-destructive-foreground text-sm">
                          ✗
                        </span>
                      </div>
                      <span className="text-base text-foreground leading-relaxed">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Progression Timeline */}
          <div className="rounded-2xl border border-border/50 bg-card p-8">
            <h3 className="mb-8 flex items-center justify-center gap-2 text-center font-bold text-2xl text-foreground">
              <TrendingUp className="h-6 w-6 text-primary" />
              İlerleme Takip Rehberi
            </h3>

            <div className="relative">
              <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-primary"></div>

              <div className="space-y-8">
                {[
                  {
                    week: "1. Hafta",
                    title: "Başlangıç Testi",
                    desc: "İlk 1RM'inizi hesaplayın",
                  },
                  {
                    week: "4. Hafta",
                    title: "İlk Kontrol",
                    desc: "İlerlemenizi değerlendirin",
                  },
                  {
                    week: "8. Hafta",
                    title: "Orta Değerlendirme",
                    desc: "Hedeflerinizi güncelleyin",
                  },
                  {
                    week: "12. Hafta",
                    title: "Final Testi",
                    desc: "Gelişiminizi ölçün",
                  },
                ].map((item, index) => (
                  <div className="relative flex items-center gap-6">
                    <div className="z-10 flex h-8 w-8 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-sm shadow-sm">
                      {index + 1}
                    </div>
                    <div className="flex-1 rounded-lg border border-border/50 bg-card/70 p-4 shadow-sm dark:border-border/30 dark:bg-card/50">
                      <div className="mb-2 flex items-center justify-between">
                        <h4 className="font-semibold text-foreground">
                          {item.title}
                        </h4>
                        <span className="font-medium text-primary text-xs">
                          {item.week}
                        </span>
                      </div>
                      <p className="text-muted-foreground text-sm">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
